import {
  KatButton,
  KatIcon,
  KatPagination,
  KatSpinner,
} from "@amzn/katal-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { NestHistoryDateFilter } from "./NestHistoryDateFilter";
import { NestHistoryTable } from "./NestHistoryTable";
import "./index.css";
import React from "react";
import type { NestUseCase, TestStatus } from "src/api/interfaces/nest";
import type { NestTestResult } from "src/api/interfaces/nest/history";
import { useNestHistoryFetcher } from "../../hooks/useNestHistoryFetcher";
import { NestHistoryDetail } from "./NestHistoryDetail";
import { useLanguage } from "src/components/Contexts/LanguageContext";

interface NestHistoryProps {
  useCase: NestUseCase;
  onNewTest: (useCaseId: string) => void;
  onClose?: () => void;
}

type PaginationState = {
  pageIndex: number;
  pageSize: number;
};

export default function NestHistory({
  useCase,
  onNewTest,
  onClose,
}: NestHistoryProps) {
  const { t } = useTranslation();
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 1,
    pageSize: 10,
  });
  const [dateRange, setDateRange] = useState<{ from?: Date; to?: Date }>({});
  const [status, setStatus] = useState<TestStatus>();
  const [selectedTest, setSelectedTest] = useState<NestTestResult | null>(null);
  const { selectedLanguage } = useLanguage();
  // biome-ignore lint/correctness/useExhaustiveDependencies: <When we change to another use case we need to reset everything>
  useEffect(() => {
    setSelectedTest(null);
    setPagination({ pageIndex: 1, pageSize: 10 });
    setDateRange({});
    setStatus(undefined);
  }, [useCase.id]);

  const { data, isLoading } = useNestHistoryFetcher({
    useCaseId: useCase.id,
    submissionDateGte: dateRange.from,
    submissionDateLte: dateRange.to,
    status,
  });

  const allTests = data?.tests ?? [];
  const totalItems = allTests.length;

  const start = (pagination.pageIndex - 1) * pagination.pageSize;
  const paginatedTests = allTests.slice(start, start + pagination.pageSize);
  return (
    <div className="nest-history">
      {selectedTest ? (
        <NestHistoryDetail
          test={selectedTest}
          onBack={() => setSelectedTest(null)}
          onNewTest={onNewTest}
        />
      ) : (
        <>
          <div className="nest-history-header">
            <div>
              {onClose && (
                <KatButton
                  variant="link"
                  className="nest-history-close"
                  onClick={onClose}
                  aria-label="Close history panel"
                >
                  <KatIcon name="chevron-left" size="small" />
                </KatButton>
              )}
              <p className="nest-history-header-title">
                {t("nest_history_name", {
                  name: t(useCase.name.key, useCase.name.parameters),
                })}
              </p>
            </div>
            <NestHistoryDateFilter
              dateFrom={dateRange.from}
              dateTo={dateRange.to}
              status={status}
              onDateRangeChange={(from, to) => setDateRange({ from, to })}
              onStatusChange={setStatus}
            />
          </div>
          {isLoading ? (
            <KatSpinner />
          ) : paginatedTests.length ? (
            <>
              <NestHistoryTable
                data={paginatedTests}
                onRowClick={setSelectedTest}
              />
              <KatPagination
                page={pagination.pageIndex}
                totalItems={totalItems}
                itemsPerPage={pagination.pageSize}
                variant="footer"
                onPageChanged={(e) =>
                  setPagination({ ...pagination, pageIndex: e.detail.page })
                }
                onItemsPerPageChange={(e) =>
                  setPagination({
                    pageIndex: 1,
                    pageSize: e.detail.value,
                  })
                }
                className="nest-history-table-pagination"
                locale={selectedLanguage}
              />
            </>
          ) : (
            <div className="nest-history-empty">{t("nest_history_noData")}</div>
          )}
        </>
      )}
    </div>
  );
}
